import { useEffect } from "react";
import { z } from "zod";

import { ColocaDateField } from "@/shared/components/forms/ColocaDateField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

export interface StatementRangePickerProps {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  /** ISO date the card was created — the earliest selectable day. */
  cardCreatedAt: string | undefined;
  disabled?: boolean;
}

/**
 * There is no `ColocaDateRangePicker` in the design system yet, so the range is two `ColocaDateField`s with a
 * cross-field refinement. `ColocaDateField` is RHF-bound by contract, hence the local form; the parent only
 * hears about complete, valid ranges.
 */
export const StatementRangePicker = ({
  from,
  to,
  onChange,
  cardCreatedAt,
  disabled = false,
}: StatementRangePickerProps) => {
  const { t } = useTranslations("cards");

  const schema = z
    .object({ from: z.string().min(1), to: z.string().min(1) })
    .refine((value) => value.from <= value.to, {
      path: ["to"],
      message: t("statement_error_range_invalid"),
    });

  const form = useZodForm(schema, { from, to }, "onChange");
  const values = form.watch();
  const isValid = !!values.from && !!values.to && values.from <= values.to;

  useEffect(() => {
    if (!isValid) return;
    if (values.from === from && values.to === to) return;
    onChange({ from: values.from, to: values.to });
  }, [isValid, values.from, values.to, from, to, onChange]);

  const minDate = cardCreatedAt ? new Date(cardCreatedAt) : undefined;

  return (
    <ColocaForm form={form} onSubmit={() => undefined} className="flex items-start gap-2 space-y-0">
      <ColocaDateField
        name="from"
        label={t("statement_from_label")}
        maxDate={new Date()}
        minDate={minDate && !Number.isNaN(minDate.getTime()) ? minDate : undefined}
        disabled={disabled}
      />
      <ColocaDateField
        name="to"
        label={t("statement_to_label")}
        maxDate={new Date()}
        minDate={minDate && !Number.isNaN(minDate.getTime()) ? minDate : undefined}
        disabled={disabled}
      />
    </ColocaForm>
  );
};
